import { Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import PaiListItem from 'features/ui/base-components/pai-list-item';
import PaiLink from 'features/ui/base-components/pai-link';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from '../../ui/base-components/pai-button';
import PaiTextField from '../../ui/base-components/pai-text-field';

interface Props {
  open: boolean;
  loading: boolean;
  campaignName: string;
  nameTakenError?: boolean;
  renameBeforeCreation?: boolean;

  handleCampaignNameChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClose(): void;

  handleRenameCampaign(): void;
}

const RenameCampaignDialog: React.FC<Props> = ({
  open,
  campaignName,
  nameTakenError,
  renameBeforeCreation,
  loading,
  handleCampaignNameChange,
  handleClose,
  handleRenameCampaign,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('renameCampaign')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          {renameBeforeCreation ? (
            <Stack gap={1}>
              <PaiListItem text={t('renameCampaignInstruction1')} size={'extra-large'} disablePadding={true} />
              <PaiListItem text={t('renameCampaignInstruction2')} size={'extra-large'} disablePadding={true} />
            </Stack>
          ) : null}
          <div>
            <PaiTextField value={campaignName} variant={'background'} placeholder={t('enterCampaignNameHere')} onChange={handleCampaignNameChange} />
            {nameTakenError ? <Typography className={classes.errorText}>{t('nameAlreadyTakenError')}</Typography> : null}
          </div>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton
          text={t('ok')}
          size={'small'}
          border={'rounded'}
          action={handleRenameCampaign}
          disabled={campaignName.trim() === ''}
          loading={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '20px 24px !important',
    whiteSpace: 'pre-wrap',
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
  errorText: {
    fontSize: theme.typography.pxToRem(11),
    color: 'red',
    marginTop: '4px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default RenameCampaignDialog;
